import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_b7533cee-6cc1-4a41-9e88-4b20184fec89/artifacts/eixt3fs2_STM%20mobile%20logo.png';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'expired'
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (token && email) {
      verifyEmail();
    } else {
      setStatus('error');
      setMessage('Invalid verification link. Please check your email for the correct link.');
    }
  }, [token, email]);

  useEffect(() => {
    // Countdown timer for resend cooldown
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const verifyEmail = async () => {
    try {
      setStatus('verifying');
      const response = await axios.post(`${API_BASE}/api/auth/verify-email`, {
        token,
        email
      });
      
      setStatus('success');
      setMessage(response.data.message || 'Email verified successfully!');
      
      // Auto-redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      const errorData = error.response?.data?.detail;
      const errorCode = errorData?.code;
      
      if (errorCode === 'TOKEN_EXPIRED') {
        setStatus('expired');
        setMessage(errorData?.error || 'Verification link has expired.');
      } else {
        setStatus('error');
        setMessage(errorData?.error || 'Failed to verify email. Please try again.');
      }
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !email) return;
    
    setResending(true);
    try {
      await axios.post(`${API_BASE}/api/auth/resend-verification`, { email });
      toast.success('Verification email sent! Please check your inbox.');
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      const errorData = error.response?.data?.detail;
      if (errorData?.code === 'RATE_LIMITED') {
        setResendCooldown(errorData.retry_after || 60);
        toast.error(errorData.error);
      } else if (errorData?.code === 'DAILY_LIMIT_REACHED') {
        toast.error(errorData.error);
      } else {
        toast.error('Failed to resend verification email. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{
        background: 'linear-gradient(135deg, #0B0F1A 0%, #0E1022 50%, #12162B 100%)'
      }}
    >
      {/* Subtle background glow */}
      <div 
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(124, 92, 255, 0.08) 0%, transparent 60%)'
        }}
      />

      <div className="w-full max-w-md relative z-10">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-4">
            <img 
              src={LOGO_URL} 
              alt="ScopeTheMarket" 
              className="h-10 w-auto"
              style={{ filter: 'drop-shadow(0 0 10px rgba(59, 130, 246, 0.5))' }}
            />
            <span className="text-xl font-semibold" style={{ color: '#F4F4F5' }}>
              ScopeTheMarket
            </span>
          </Link>
        </div>

        {/* Verification Card */}
        <div 
          className="rounded-2xl p-8"
          style={{
            backgroundColor: 'rgba(30, 41, 59, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Verifying State */}
          {status === 'verifying' && (
            <div className="text-center">
              <Loader2 className="h-16 w-16 mx-auto mb-4 animate-spin" style={{ color: '#3B82F6' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#F4F4F5' }}>
                Verifying your email...
              </h2>
              <p style={{ color: '#94A3B8' }}>
                Please wait while we verify your email address.
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center">
              <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: '#22C55E' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#F4F4F5' }}>
                Email Verified!
              </h2>
              <p className="mb-6" style={{ color: '#94A3B8' }}>
                {message}
              </p>
              <p className="text-sm mb-4" style={{ color: '#64748B' }}>
                Redirecting to login...
              </p>
              <Link 
                to="/login"
                className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-all"
                style={{ backgroundColor: '#3B82F6', color: 'white' }}
              >
                Continue to Login
              </Link>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center">
              <XCircle className="h-16 w-16 mx-auto mb-4" style={{ color: '#EF4444' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#F4F4F5' }}>
                Verification Failed
              </h2>
              <p className="mb-6" style={{ color: '#94A3B8' }}>
                {message}
              </p>
              <div className="space-y-3">
                {email && (
                  <button
                    onClick={handleResend}
                    disabled={resending || resendCooldown > 0}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all disabled:opacity-50"
                    style={{ backgroundColor: '#3B82F6', color: 'white' }}
                  >
                    {resending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4" />
                    )}
                    {resendCooldown > 0 
                      ? `Resend in ${resendCooldown}s` 
                      : 'Resend Verification Email'}
                  </button>
                )}
                <Link 
                  to="/login"
                  className="block w-full text-center px-6 py-3 rounded-lg font-medium transition-all"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#F4F4F5' }}
                >
                  Back to Login
                </Link>
              </div>
            </div>
          )}

          {/* Expired State */}
          {status === 'expired' && (
            <div className="text-center">
              <Mail className="h-16 w-16 mx-auto mb-4" style={{ color: '#F59E0B' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#F4F4F5' }}>
                Link Expired
              </h2>
              <p className="mb-6" style={{ color: '#94A3B8' }}>
                {message}
              </p>
              <div className="space-y-3">
                {email && (
                  <button
                    onClick={handleResend}
                    disabled={resending || resendCooldown > 0}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all disabled:opacity-50"
                    style={{ backgroundColor: '#3B82F6', color: 'white' }}
                  >
                    {resending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4" />
                    )}
                    {resendCooldown > 0 
                      ? `Resend in ${resendCooldown}s` 
                      : 'Send New Verification Link'}
                  </button>
                )}
                <Link 
                  to="/login"
                  className="block w-full text-center px-6 py-3 rounded-lg font-medium transition-all"
                  style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#F4F4F5' }}
                >
                  Back to Login
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Help text */}
        <p className="text-center mt-6 text-sm" style={{ color: '#64748B' }}>
          Having trouble? <a href="mailto:support@scopethemarket.com" className="underline" style={{ color: '#3B82F6' }}>Contact support</a>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmail;
